import { _createPromiseDecorator } from '@naturalcycles/js-lib'
import { di } from '@src/app/core/services/di.service.js'
import { ErrorService } from '@src/app/core/services/error.service.js'
import { SignalStore } from '@src/app/core/store/signalStore.js'
import { sentry } from '@src/app/core/util/sentry.util.js'
import { ErrorHandlerType } from '@src/app/shared/typings/enum/error-handler.js'
import { LoaderType } from '@src/app/shared/typings/ui.js'

interface LoaderDecoratorParams {
  type: LoaderType
}

interface ErrorHandlerDecoratorParams {
  type: ErrorHandlerType
}

interface DecorateDecoratorParams {
  errorHandlerType?: ErrorHandlerType
  loaderType?: LoaderType
}

// eslint-disable-next-line @typescript-eslint/naming-convention
export const Loader = (
  params: LoaderDecoratorParams = { type: LoaderType.BLOCKING },
): MethodDecorator =>
  _createPromiseDecorator(
    {
      decoratorName: 'Loader',
      beforeFn: ({ decoratorParams: { type } }) => showLoader(type),
      finallyFn: ({ decoratorParams: { type } }) => hideLoader(type),
    },
    params,
  )

// eslint-disable-next-line @typescript-eslint/naming-convention
export const ErrorHandler = (
  params: ErrorHandlerDecoratorParams = { type: ErrorHandlerType.DIALOG },
): MethodDecorator =>
  _createPromiseDecorator(
    {
      decoratorName: 'ErrorHandler',
      catchFn: ({ err, decoratorParams: { type } }) => handleError(err, type),
    },
    params,
  )

/**
 * Decorator that combines Loader and ErrorHandler.
 * It's useful to not need to remember the order of these 2 decorators (Loader and ErrorHandler).
 * If order is used wrongly, BlockingLoader may still spin while ErrorHandler is showing the dialog.
 */
// eslint-disable-next-line @typescript-eslint/naming-convention
export const Decorate = (params: DecorateDecoratorParams): MethodDecorator =>
  _createPromiseDecorator(
    {
      decoratorName: 'Decorate',
      beforeFn: ({ decoratorParams: { loaderType } }) => showLoader(loaderType),
      thenFn: ({ decoratorParams: { loaderType } }) => hideLoader(loaderType),
      catchFn: ({ err, decoratorParams: { loaderType, errorHandlerType } }) => {
        hideLoader(loaderType)
        handleError(err, errorHandlerType || ErrorHandlerType.LOG)
      },
    },
    params,
  )

function showLoader(loaderType?: LoaderType): void {
  if (!loaderType) return
  if (loaderType === LoaderType.BLOCKING) {
    di.get(SignalStore).$loading.set(true)
  } else {
    di.get(SignalStore).$ghost.set(true)
  }
}

function hideLoader(loaderType?: LoaderType): void {
  if (!loaderType) return
  if (loaderType === LoaderType.BLOCKING) {
    di.get(SignalStore).$loading.set(false)
  } else {
    di.get(SignalStore).$ghost.set(false)
  }
}

function handleError(err: any, errorHandlerType?: ErrorHandlerType): void {
  if (!errorHandlerType) return
  sentry.captureException(err)

  if (errorHandlerType === ErrorHandlerType.DIALOG) {
    void di.get(ErrorService).showErrorDialog(err)
  } else {
    di.get(ErrorService).logError(err)
  }

  // suppress error, don't propagate further, resolve(undefined)
}
